import { createPortal } from "react-dom";
import { Calculator as CalcIcon, X } from "lucide-react";
import { evaluateExpression } from "@/lib/calculatorUtils";

// Popup + focus hint UI for CalculatorOverlay — pure presentational, extracted for
// readability. All state/logic stays owned by CalculatorOverlay.
export function CalculatorHint({ show, anchor }) {
  if (!show || !anchor?.getBoundingClientRect) return null;
  return createPortal(
    <div
      style={{
        position: "fixed",
        left: Math.max(4, anchor.getBoundingClientRect().right - 90),
        top: Math.max(4, anchor.getBoundingClientRect().top - 24),
        zIndex: 9998,
        pointerEvents: "none",
      }}
      className="text-[10px] bg-zinc-900 text-white px-1.5 py-0.5 rounded shadow flex items-center gap-1 opacity-90"
      data-testid="calc-hint"
    >
      press <kbd className="bg-white text-zinc-900 px-1 rounded font-mono">X</kbd> or <kbd className="bg-white text-zinc-900 px-1 rounded font-mono">=</kbd>
      <CalcIcon className="w-2.5 h-2.5" />
    </div>,
    document.body,
  );
}

export function CalculatorPopup({ anchorRect, expr, setExpr, exprRef, onCommit, onCancel }) {
  return createPortal(
    <>
      <div
        style={{ position: "fixed", inset: 0, zIndex: 9998 }}
        onMouseDown={onCancel}
        data-testid="calc-backdrop"
      />
      <div
        style={{
          position: "fixed",
          left: Math.max(8, Math.min(window.innerWidth - 280, anchorRect.left)),
          top: Math.min(window.innerHeight - 160, anchorRect.bottom + 4),
          zIndex: 9999,
          width: 260,
        }}
        className="bg-white border border-zinc-300 rounded-lg shadow-xl p-3"
        data-testid="calc-popup"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-xs font-semibold flex items-center gap-1">
            <CalcIcon className="w-3.5 h-3.5" /> Calculator
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="text-zinc-400 hover:text-zinc-800"
            data-testid="calc-close-btn"
            aria-label="Close"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
        <input
          ref={exprRef}
          value={expr}
          onChange={(e) => setExpr(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); onCommit(); }
            else if (e.key === "Escape") { e.preventDefault(); onCancel(); }
          }}
          placeholder="e.g.  12.50 + 3.99 * 2"
          className="w-full border border-zinc-300 rounded px-2 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-400"
          data-no-calc="1"
          data-testid="calc-expr-input"
        />
        <div className="mt-1 text-[10px] text-zinc-500">
          {evaluateExpression(expr) !== null ? (
            <>= <span className="font-mono font-semibold text-zinc-800" data-testid="calc-preview">{evaluateExpression(expr)}</span></>
          ) : (
            <>digits and + − × ÷ ( ) only · Enter = apply · Esc = cancel</>
          )}
        </div>
        <div className="mt-2 flex gap-1.5 justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="text-xs px-2.5 py-1 rounded border border-zinc-300 hover:bg-zinc-50"
            data-testid="calc-cancel-btn"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onCommit}
            disabled={evaluateExpression(expr) === null}
            className="text-xs px-3 py-1 rounded bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="calc-apply-btn"
          >
            = Apply
          </button>
        </div>
      </div>
    </>,
    document.body,
  );
}
